import { useMemo } from 'react';

import { useCustomerDispatch } from '../../../customer/dispatch';
import {
  applyCrmRedemption,
  attemptToAddToCart,
  checkout,
  deleteCartItem,
  deleteUnavailableProducts,
  removeCrmRedemption,
  resetCart,
  updateCart,
  updateDeliveryAddress,
  updateQueryPromoCode,
  updateReservationMode,
} from '../../../customer/redux/cart';

/**
 * The write side of the cart facade. Each action forwards to the corresponding v1
 * Redux thunk; the facade name is the canonical v3 name, which differs from the
 * legacy export. Consumers migrate onto these names now so the bodies can be
 * swapped for v3 mutations later without touching call sites.
 * TODO(carts-v3): re-point each action at the v3 mutation once the backing exists.
 */
export interface CartFacadeActions {
  /**
   * The consumer-level add. Forwards `attemptToAddToCart`, which owns the
   * store-switch guard, the add-vs-update branch, and the add-to-cart analytics —
   * not the inner `addToCart` thunk (which has none of those). A cart is still
   * created implicitly on first add inside `attemptToAddToCart → addToCart`.
   */
  addProduct: (...args: Parameters<typeof attemptToAddToCart>) => void;
  applyLoyalty: (...args: Parameters<typeof applyCrmRedemption>) => void;
  applyPromoCode: (...args: Parameters<typeof updateQueryPromoCode>) => void;
  checkout: (...args: Parameters<typeof checkout>) => void;
  createCart: () => void;
  deleteUnavailableProducts: (
    ...args: Parameters<typeof deleteUnavailableProducts>
  ) => void;
  removeLoyalty: (...args: Parameters<typeof removeCrmRedemption>) => void;
  removeProduct: (...args: Parameters<typeof deleteCartItem>) => void;
  resetCart: (...args: Parameters<typeof resetCart>) => void;
  updateDeliveryAddress: (
    ...args: Parameters<typeof updateDeliveryAddress>
  ) => void;
  updateProduct: (...args: Parameters<typeof updateCart>) => void;
  updateReservationMode: (
    ...args: Parameters<typeof updateReservationMode>
  ) => void;
}

export const useCartActions = (): CartFacadeActions => {
  const dispatch = useCustomerDispatch();

  return useMemo(
    () => ({
      addProduct: (...args) => dispatch(attemptToAddToCart(...args)),
      updateProduct: (...args) => dispatch(updateCart(...args)),
      removeProduct: (...args) => dispatch(deleteCartItem(...args)),
      applyPromoCode: (...args) => dispatch(updateQueryPromoCode(...args)),
      updateReservationMode: (...args) =>
        dispatch(updateReservationMode(...args)),
      updateDeliveryAddress: (...args) =>
        dispatch(updateDeliveryAddress(...args)),
      applyLoyalty: (...args) => dispatch(applyCrmRedemption(...args)),
      removeLoyalty: (...args) => dispatch(removeCrmRedemption(...args)),
      deleteUnavailableProducts: (...args) =>
        dispatch(deleteUnavailableProducts(...args)),
      checkout: (...args) => dispatch(checkout(...args)),
      resetCart: (...args) => dispatch(resetCart(...args)),
      // A cart is created implicitly on first add — there is no standalone create
      // thunk. `createCart` exists on the interface because consumers (and the
      // future v3 backing) expect it, but on the v1 Redux path it is a no-op: the
      // real network create happens inside `addProduct`/`addToCart`. It must NOT
      // dispatch `initiateCreateCart` — that reducer sets `isLoading: true` and the
      // only reducer that clears it is `createHeadlessCartSuccess` (the headless
      // thunk's success). Without an immediately-following headless create, that
      // would strand `isLoading`/`isFetching` stuck true.
      createCart: () => undefined,
    }),
    [dispatch]
  );
};
